import { useMemo, useState } from 'react'
import {
  Alert, Card, Table, Tag, Typography, Select, Row, Col, Statistic, Space, Tooltip,
} from 'antd'
import {
  ApartmentOutlined, ArrowDownOutlined, ArrowRightOutlined, ArrowUpOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

// Sprint 0144 — Stage aliases match backend dept codes.
// Multiple DB dept codes can roll up into one display stage
// (DRAW_FRAME_BREAKER + DRAW_FRAME_FINISHER + LAP_FORMER → DRAWING).
const STAGE_DISPLAY: Array<{
  key: string  // canonical key used in this UI
  label: string
  aliases: string[]  // dept codes from backend that roll up to this stage
  color: string
  bg: string
}> = [
  { key: 'GODOWN',     label: 'GODOWN',     aliases: ['GODOWN'],
    color: '#1677ff', bg: '#e6f4ff' },
  { key: 'BLOW_ROOM',  label: 'BLOW ROOM',  aliases: ['BLOW_ROOM', 'BLOWROOM'],
    color: '#fa8c16', bg: '#fff7e6' },
  { key: 'CARDING',    label: 'CARDING',    aliases: ['CARDING'],
    color: '#722ed1', bg: '#f9f0ff' },
  { key: 'COMBER',     label: 'COMBER',     aliases: ['COMBER'],
    color: '#9254de', bg: '#f0e7fb' },
  { key: 'DRAWING',    label: 'DRAWING',
    aliases: ['DRAW_FRAME_BREAKER', 'DRAW_FRAME_FINISHER', 'DRAWING', 'LAP_FORMER'],
    color: '#13c2c2', bg: '#e6fffb' },
  { key: 'SIMPLEX',    label: 'SIMPLEX',    aliases: ['SIMPLEX'],
    color: '#eb2f96', bg: '#fff0f6' },
  { key: 'RING_FRAME', label: 'RING FRAME', aliases: ['RING_FRAME'],
    color: '#faad14', bg: '#fffbe6' },
  { key: 'AUTO_CONER', label: 'AUTO CONER', aliases: ['AUTO_CONER', 'AUTOCONER'],
    color: '#a0d911', bg: '#fcffe6' },
  { key: 'FG_STOCK',   label: 'FG STOCK',   aliases: ['FG_STOCK', 'FG_STORE'],
    color: '#52c41a', bg: '#f6ffed' },
]
const STAGES = STAGE_DISPLAY.map((s) => s.key)
const STAGE_COLORS: Record<string, string> = Object.fromEntries(STAGE_DISPLAY.map((s) => [s.key, s.color]))
const STAGE_BG: Record<string, string> = Object.fromEntries(STAGE_DISPLAY.map((s) => [s.key, s.bg]))

// Map a backend stage/dept code to its canonical UI key.
function canonicalStage(raw: string | null | undefined): string | null {
  if (!raw) return null
  const up = String(raw).toUpperCase()
  for (const d of STAGE_DISPLAY) {
    if (d.aliases.includes(up)) return d.key
  }
  return up  // unknown — show as-is, won't be in cards but visible in table
}

export default function SectionStockPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [stageFilter, setStageFilter] = useState<string | undefined>()

  const { data: sectionStock, isLoading } = useQuery({
    queryKey: ['section-stock', plantId],
    queryFn: () =>
      apiClient.get(`${API}/section-stock`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  // Sprint 0144 — Today's flow per section (produced + transferred).
  // Surfaces the live link from Daily Shift Log → Section Stock.
  const { data: flowToday } = useQuery({
    queryKey: ['section-flow-today', plantId],
    queryFn: () =>
      apiClient.get(`${API}/section-stock/flow-today`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
    refetchInterval: 60_000,
  })

  const stockList = Array.isArray(sectionStock) ? sectionStock : []
  const flowList: Array<{
    stage: string; n_logs: number;
    produced_today_kg: number; transferred_today_kg: number
  }> = Array.isArray(flowToday) ? flowToday : []

  // Aggregate per canonical stage — handles all aliases (BLOW_ROOM, DRAW_FRAME_*, etc.)
  const stageAgg: Record<string, {
    total_kg: number; lot_count: number;
    produced_today_kg: number; transferred_today_kg: number;
    has_synthetic: boolean;
  }> = {}
  STAGES.forEach(s => {
    stageAgg[s] = {
      total_kg: 0, lot_count: 0,
      produced_today_kg: 0, transferred_today_kg: 0,
      has_synthetic: false,
    }
  })
  stockList.forEach((s: any) => {
    const key = canonicalStage(s.stage)
    if (key && stageAgg[key]) {
      stageAgg[key].total_kg += Number(s.balance_kg ?? 0)
      stageAgg[key].lot_count += 1
      // Synthetic rows have id=null (computed, not stored)
      if (s.id == null) stageAgg[key].has_synthetic = true
    }
  })
  flowList.forEach((f) => {
    const key = canonicalStage(f.stage)
    if (key && stageAgg[key]) {
      stageAgg[key].produced_today_kg += Number(f.produced_today_kg ?? 0)
      stageAgg[key].transferred_today_kg += Number(f.transferred_today_kg ?? 0)
    }
  })

  // Filter list — match by canonical key so user-selected DRAWING shows
  // all DRAW_FRAME_BREAKER + DRAW_FRAME_FINISHER + LAP_FORMER rows.
  const filteredList = stageFilter
    ? stockList.filter((s: any) => canonicalStage(s.stage) === stageFilter)
    : stockList

  const anySynthetic = useMemo(
    () => stockList.some((s: any) => s.id == null),
    [stockList],
  )

  const columns = [
    {
      title: 'Stage', dataIndex: 'stage', key: 'stage', width: 150,
      render: (v: string, row: any) => {
        const key = canonicalStage(v) ?? v
        return (
          <Space size={4}>
            <span style={{ color: STAGE_COLORS[key] ?? '#000', fontWeight: 600 }}>
              {v?.replace(/_/g, ' ')}
            </span>
            {row.id == null && (
              <Tooltip title="Computed from production logs (not yet IDT-transferred)">
                <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>auto</Tag>
              </Tooltip>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Material Type', dataIndex: 'material_type', key: 'material_type', width: 140,
      render: (v: string) => v?.replace(/_/g, ' ') ?? '—',
    },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot_no', width: 130 },
    { title: 'Grade', dataIndex: 'grade', key: 'grade', width: 70 },
    {
      title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count_ne', width: 90, align: 'right' as const,
      render: (v: number) => v ?? '—',
    },
    {
      title: 'Balance (kg)', dataIndex: 'balance_kg', key: 'balance_kg', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Balance (bales)', dataIndex: 'balance_bales', key: 'balance_bales', width: 120, align: 'right' as const,
      render: (v: number) => v ?? '—',
    },
    {
      title: 'Cost ₹/kg', dataIndex: 'cost_per_kg', key: 'cost_per_kg', width: 110, align: 'right' as const,
      render: (v: number | null) => v != null ? v.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—',
    },
    {
      title: 'WIP Value ₹', dataIndex: 'total_value', key: 'total_value', width: 130, align: 'right' as const,
      render: (v: number | null) => v != null ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—',
    },
    // PROD-002D — spinning stage physical units (bobbin/cop/cone) alongside kg.
    { title: 'Units', key: 'units', align: 'right' as const,
      render: (_: unknown, r: any) => r.balance_units != null && r.unit_type
        ? `${r.balance_units.toLocaleString('en-IN')} ${r.unit_type.toLowerCase()}` : '—' },
    {
      title: 'Last Updated', dataIndex: 'updated_at', key: 'updated_at', width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY HH:mm') : '—',
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ApartmentOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Section Stock
      </Title>
      <Typography.Text type="secondary">In-process material at each production stage</Typography.Text>

      {/* Plant selector */}
      <Row gutter={12} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 180 }}
            options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
          />
        </Col>
      </Row>

      {/* Sprint 0144 — Data wiring banner.
          Tells the user where these numbers come from so they trust
          the chain. Shows when synthetic rows are present (mill not
          using IDT module). */}
      {anySynthetic && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Auto-computed from production logs"
          description={
            <span>
              Stock balances reflect{' '}
              <b>Σ produced − Σ transferred</b> from{' '}
              <a href="/shop-floor/daily-shift-log">Daily Shift Log</a>
              {' '}entries. Each section card also shows today's
              produced + transferred kg, so the link from production
              to stock is visible live. GODOWN comes from{' '}
              <a href="/bale-mgmt">bales</a>, FG STOCK from{' '}
              <a href="/fg-stock">fg_lots</a>.
            </span>
          }
        />
      )}

      {/* Flow Diagram */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
        <Title level={5} style={{ marginBottom: 12 }}>
          Production Flow
          <Typography.Text type="secondary" style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            (today's in/out shown under each section)
          </Typography.Text>
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 4 }}>
          {STAGES.map((stage, idx) => {
            const agg = stageAgg[stage]
            const display = STAGE_DISPLAY.find((d) => d.key === stage)!
            return (
              <Space key={stage} size={4} align="center">
                <Card
                  size="small"
                  style={{
                    minWidth: 130,
                    borderColor: STAGE_COLORS[stage],
                    background: STAGE_BG[stage],
                    cursor: 'pointer',
                    borderWidth: 2,
                  }}
                  styles={{ body: { padding: '8px 12px' } }}
                  onClick={() => setStageFilter(stageFilter === stage ? undefined : stage)}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: STAGE_COLORS[stage] }}>
                    {display.label}
                  </div>
                  <Statistic
                    value={agg.total_kg.toFixed(0)}
                    suffix="kg"
                    valueStyle={{ fontSize: 18, color: STAGE_COLORS[stage] }}
                  />
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {agg.lot_count} lots
                  </div>
                  {/* Sprint 0144 — Today's flow into / out of this section */}
                  {(agg.produced_today_kg > 0 || agg.transferred_today_kg > 0) && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed #ddd', fontSize: 11 }}>
                      {agg.produced_today_kg > 0 && (
                        <Tooltip title="Produced today (from Daily Shift Log)">
                          <span style={{ color: '#52c41a' }}>
                            <ArrowDownOutlined /> {agg.produced_today_kg.toFixed(0)} kg
                          </span>
                        </Tooltip>
                      )}
                      {agg.transferred_today_kg > 0 && (
                        <Tooltip title="Transferred today to next section">
                          <span style={{ color: '#fa541c', marginLeft: 8 }}>
                            <ArrowUpOutlined /> {agg.transferred_today_kg.toFixed(0)} kg
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </Card>
                {idx < STAGES.length - 1 && (
                  <ArrowRightOutlined style={{ color: '#bbb', fontSize: 16, flexShrink: 0 }} />
                )}
              </Space>
            )
          })}
        </div>
      </Card>

      {/* Detail Table */}
      <Card>
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col>
            <Select
              allowClear
              placeholder="Filter by Stage"
              style={{ width: 180 }}
              value={stageFilter}
              onChange={v => setStageFilter(v)}
              options={STAGE_DISPLAY.map(s => ({ value: s.key, label: s.label }))}
            />
          </Col>
          {stageFilter && (
            <Col>
              <Typography.Text type="secondary">
                Showing: <b>{STAGE_DISPLAY.find((d) => d.key === stageFilter)?.label ?? stageFilter}</b>
              </Typography.Text>
            </Col>
          )}
        </Row>

        <Table
          dataSource={filteredList}
          columns={columns}
          // Sprint 0144 — Synthetic rows have id=null; build a composite
          // key so rows still render distinctly.
          rowKey={(r: any) => r.id ?? `${r.stage}-${r.lot_no ?? '_'}-${r.grade ?? '_'}-${r.balance_kg ?? 0}`}
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1250 }}
        />
      </Card>
    </div>
  )
}
